import * as echarts from "echarts";
import React, { useEffect, useMemo, useRef } from "react";
import "echarts-wordcloud";

interface KeywordChartProps {
  keywords: Array<{ text: string; weight: number; keywordId?: string }>;
  height?: number;
  title?: string;
  colors?: string[];
  minFontSize?: number;
  maxFontSize?: number;
  shape?:
    | "circle"
    | "cardioid"
    | "diamond"
    | "triangle-forward"
    | "triangle"
    | "pentagon"
    | "star";
  onKeywordClick?: (name: string, weight: number, keywordId?: string) => void;
}

const KeywordChart: React.FC<KeywordChartProps> = ({
  keywords,
  height = 320,
  title,
  colors = [
    "#2563eb",
    "#4BA67A",
    "#ef4444",
    "#f59e0b",
    "#8b5cf6",
    "#06b6d4",
    "#dc2626",
  ],
  minFontSize,
  maxFontSize,
  shape = "circle",
  onKeywordClick,
}) => {
  const chartRef = useRef<HTMLDivElement>(null);
  const chartInst = useRef<echarts.ECharts | null>(null);
  const isChartInitialized = useRef(false);

  // 响应式字体大小配置：PC端和移动端使用不同配置
  const [currentFontSizes, setCurrentFontSizes] = React.useState({
    min: 8,
    max: 28,
  });

  // 图表初始化和响应式监听（合并优化）
  useEffect(() => {
    if (!chartRef.current) return;
    if (!chartInst.current) {
      chartInst.current = echarts.init(chartRef.current, {
        // 禁用所有动画
        animation: false,
        useDirtyRect: false,
      });
    }

    // 更新字体大小的函数
    const updateFontSizes = () => {
      const isMobile = window.innerWidth < 768;
      const newMinFontSize =
        minFontSize !== undefined ? minFontSize : isMobile ? 8 : 10;
      const newMaxFontSize =
        maxFontSize !== undefined ? maxFontSize : isMobile ? 26 : 30;
      setCurrentFontSizes({ min: newMinFontSize, max: newMaxFontSize });
    };

    // 使用防抖优化重绘性能
    let resizeTimeout: number;
    const handleResize = () => {
      clearTimeout(resizeTimeout);
      resizeTimeout = window.setTimeout(() => {
        updateFontSizes(); // 更新字体大小
        chartInst.current?.resize({ animation: { duration: 0 } }); // 禁用resize动画
      }, 150);
    };

    // 初始化字体大小
    updateFontSizes();

    window.addEventListener("resize", handleResize);
    return () => {
      window.removeEventListener("resize", handleResize);
      clearTimeout(resizeTimeout);
    };
  }, [minFontSize, maxFontSize]);

  // 使用 useMemo 缓存图表数据，避免不必要的重计算
  const chartData = useMemo(() => {
    if (!keywords || keywords.length === 0) return [];

    const hashToIndex = (s: string, mod: number) => {
      let h = 0;
      for (let i = 0; i < s.length; i++) h = (h * 131 + s.charCodeAt(i)) >>> 0;
      return h % Math.max(1, mod);
    };

    return keywords
      .filter((k) => k && k.text && Number.isFinite(k.weight))
      .map((k) => {
        const idx = hashToIndex(k.text, colors.length);
        const color = colors[idx];

        const textLength = k.text.length;
        const displayText = k.text;

        // if (textLength > 16) {
        //   displayText = k.text.substring(0, 13) + "...";
        // }

        const baseFontSize = Math.max(
          currentFontSizes.min,
          Math.min(
            currentFontSizes.max,
            currentFontSizes.max * (1 - Math.min(0.7, (textLength - 5) / 15))
          )
        );

        const adjustedWeight =
          textLength > 5
            ? k.weight * Math.max(0.1, 1 - (textLength - 5) / 10)
            : k.weight;

        return {
          name: displayText,
          value: Math.max(0, adjustedWeight),
          textStyle: { color },
          fontSize: baseFontSize,
          originalText: k.text,
          keywordId: k.keywordId,
        };
      });
  }, [keywords, colors, currentFontSizes]);

  // 使用 useMemo 缓存图表配置
  const chartOption = useMemo(() => {
    if (!chartData || chartData.length === 0) return null;

    const data = [...chartData];
    const allEqual = data.every((d) => d.value === data[0].value);
    if (allEqual)
      data.forEach((d, i) => {
        d.value = d.value + i * 0.0001;
      });

    return {
      title: title
        ? {
            left: "center",
            textStyle: { fontSize: 16, fontWeight: 400, color: "#111827" },
          }
        : undefined,
      tooltip: {
        show: true,
        trigger: "item",
        formatter: (params: any) => {
          const data = params.data;
          const originalText = data.originalText || data.name;
          return originalText;
        },
      },
      series: [
        {
          type: "wordCloud",
          shape,
          gridSize: 8,
          sizeRange: [currentFontSizes.min, currentFontSizes.max],
          rotationRange: [0, 0],
          width: "100%",
          height: "100%",
          drawOutOfBound: false,
          textStyle: {
            fontFamily: "Arial, sans-serif",
            fontWeight: "normal",
          },
          emphasis: {
            focus: "none",
            textStyle: {
              color: (p: any) => p?.data?.textStyle?.color,
              shadowBlur: 7,
              shadowColor: "rgba(0, 0, 0, 0.3)",
            },
          },
          data,
          layoutAnimation: false, // 解决点击闪烁问题
          textPadding: 2,
          autoSize: {
            enable: true,
            minSize: 6,
          },
        },
      ],
    } as echarts.EChartsCoreOption;
  }, [chartData, title, shape, currentFontSizes]);

  // 图表渲染逻辑（优化版本）
  useEffect(() => {
    if (!chartInst.current) return;

    if (!chartData || chartData.length === 0) {
      chartInst.current.clear();
      return;
    }

    if (!chartOption) return;

    try {
      // 使用 notMerge: true 确保完全替换而不是合并，避免闪烁
      chartInst.current.setOption(chartOption, true);
      isChartInitialized.current = true;
    } catch (err) {
      console.error("KeywordChart setOption error:", err);
    }
  }, [chartOption]);

  // 点击事件处理（独立监听）
  useEffect(() => {
    if (!chartInst.current || !onKeywordClick) return;

    const handleClick = (params: any) => {
      const originalText = params?.data?.originalText || params?.data?.name;
      const value = params?.data?.value;
      const keywordId = params?.data?.keywordId;
      if (originalText != null && value != null) {
        // 不再尝试阻止事件冒泡，避免兼容性问题
        onKeywordClick(String(originalText), Number(value), keywordId);
      }
    };

    chartInst.current.off("click");
    chartInst.current.on("click", handleClick);

    // 清理函数
    return () => {
      if (chartInst.current) {
        chartInst.current.off("click", handleClick);
      }
    };
  }, [onKeywordClick]);

  // 添加一个useEffect来确保容器宽度正确
  useEffect(() => {
    const resizeObserver = new ResizeObserver((entries) => {
      if (chartInst.current) {
        chartInst.current.resize({ animation: { duration: 0 } }); // 禁用resize动画
      }
    });

    if (chartRef.current) {
      resizeObserver.observe(chartRef.current);
    }

    return () => {
      resizeObserver.disconnect();
      chartInst.current?.dispose();
      chartInst.current = null;
    };
  }, []);

  return (
    <div
      style={{
        border: "1px solid #e5e7eb",
        borderRadius: "6px",
        padding: "16px",
        background: "#ffffff",
        width: "100%", // 确保容器宽度为100%
        height: "100%",
      }}
    >
      {title && (
        <div
          style={{
            fontWeight: "normal",
            marginBottom: 8,
            color: "#111827",
            fontSize: 16,
          }}
        >
          {title}
        </div>
      )}
      <div
        ref={chartRef}
        style={{ width: "100%", height: "100%", minHeight: height }}
      />
    </div>
  );
};

export default KeywordChart;
